import { getPriorities, getLeaveTypes, theme } from '@utils/auth'
import { getTagStyleFromColor } from '@utils/color'
import api from '@api'
import Moment from 'moment'
import Constants from '@constants'
import {
  buildFlatQualificationStructure,
  buildRelationalQualificationStructure,
} from '@data/qualification'

import {
  getRootTranslator,
  getRootPluaralTranslator,
  // getModuleTranslator,
} from '@/src/utils/get-module-translator'

const __rootT = getRootTranslator()
const __rootTc = getRootPluaralTranslator()

export function prepareEventColors(id, options) {
  const item = options.find((i) => i.id === id)
  if (item) {
    const style = getTagStyleFromColor(item.color, theme())
    return {
      textColor: style.color,
      borderColor: style.backgroundColor,
      backgroundColor: style.backgroundColor,
    }
  }
  return {}
}

function transformUserLeave(leave) {
  return {
    id: leave.id,
    name: leave.name,
    technicianId: leave.technicianId,
    leaveTypeId: leave.leaveTypeId,
    startDate: leave.startDate,
    endDate: leave.endDate,
    remarks: leave.remarks,
    enableDelegation: leave.enableDelegation,
    workDelegatedTo: leave.workDelegatedTo,
    workDelegationGroupId: leave.workDelegationGroupId,
    delegationStartDate: leave.delegationStartDate,
    delegationEndDate: leave.delegationEndDate,
    delegatedWorkModels: leave.delegatedWorkModels,
  }
}

export const initialEventAdapterForCalendar = (items, selectedModule) => {
  return items.map((item) => {
    const options =
      selectedModule.key === 'user_leave' ? getLeaveTypes() : getPriorities()
    return {
      id: item.id,
      title: `${item.name}: ${item.subject || item.displayName}`,
      start: selectedModule.startEvaluationKey
        ? item[selectedModule.startEvaluationKey]
        : item[selectedModule.evaluationKey],
      end: selectedModule.startEvaluationKey
        ? Moment(item[selectedModule.evaluationKey]).endOf('day').valueOf()
        : item[selectedModule.evaluationKey],
      extendedProps: {
        ...item,
      },
      ...(selectedModule.key === 'user_leave'
        ? prepareEventColors(item.leaveTypeId, options)
        : prepareEventColors(item.priorityId, options)),
      allDay: false,
    }
  })
}
const dbCommonKeys = ['id', 'name', 'priorityId', 'createdTime']
const dbColumn = {
  [Constants.REQUEST]: ['dueBy', 'subject', ...dbCommonKeys],
  [Constants.TASK]: ['endTime', 'startTime', 'subject', ...dbCommonKeys],
  [Constants.PROBLEM]: ['dueBy', 'subject', ...dbCommonKeys],
  [Constants.CHANGE]: ['endDate', 'subject', ...dbCommonKeys],
  [Constants.RELEASE]: ['endDate', 'subject', ...dbCommonKeys],
  [Constants.PROJECT]: ['endDate', 'displayName', ...dbCommonKeys],
  user_leave: [
    'id',
    'technicianId',
    'leaveTypeId',
    'remarks',
    'startDate',
    'endDate',
  ],
}

const buildQaualification = (qualificationFactors, moduleName) => {
  const quals = []
  if (qualificationFactors.technicianId) {
    const value = Array.isArray(qualificationFactors.technicianId)
      ? qualificationFactors.technicianId
      : [qualificationFactors.technicianId]
    quals.push(
      buildRelationalQualificationStructure(
        'technicianId',
        'in',
        value,
        'long',
        'db'
      )
    )
  }
  if (qualificationFactors.companyId) {
    quals.push(
      buildRelationalQualificationStructure(
        qualificationFactors.moduleName === 'user_leave'
          ? 'my_company'
          : 'companyId',
        'in',
        qualificationFactors.companyId,
        'long',
        qualificationFactors.moduleName === 'user_leave' ? 'variable' : 'db'
      )
    )
  }
  if (qualificationFactors.groupId) {
    quals.push(
      buildRelationalQualificationStructure(
        qualificationFactors.moduleName === 'user_leave'
          ? 'leave_group'
          : 'groupId',
        'equal',
        qualificationFactors.groupId,
        'long',
        qualificationFactors.moduleName === 'user_leave' ? 'variable' : 'db'
      )
    )
  }
  // if (qualificationFactors.startDate && qualificationFactors.endDate) {
  //   quals.push(
  //     buildRelationalQualificationStructure(
  //       qualificationFactors.evaluationKey,
  //       'between',
  //       [qualificationFactors.startDate, qualificationFactors.endDate],
  //       'long',
  //       'db'
  //     )
  //   )
  // }
  return buildFlatQualificationStructure(quals)
}

function transformCalendar(moduleName, calendar) {
  const response = {}
  dbColumn[moduleName].forEach((key) => {
    response[key] = calendar[key]
  })
  return response
}
function transformCalendarForServer(calendar, moduleName) {
  return {
    startDate: calendar.startDate,
    endDate: calendar.endDate,
    dbColumn: calendar.dbColumn ? calendar.dbColumn : dbColumn[moduleName],
    ...(calendar.dataFilter
      ? {
          qualDetails: buildQaualification(
            { ...calendar, ...calendar.dataFilter },
            moduleName
          ),
        }
      : {}),
    qualId: calendar.qualId,
    evaluationKey: calendar.qualEvaluationKey,
  }
}

export function searchCalendarApi(moduleName, data, limit, offset) {
  return api
    .post(
      `${moduleName}/calender/search`,
      transformCalendarForServer(data, moduleName),
      {
        params: {
          offset: offset || 0,
          size: limit === undefined ? 1000 : limit,
        },
        notify: false,
      }
    )
    .then((data) => {
      return {
        items: (data || []).map((i) => transformCalendar(moduleName, i)),
      }
    })
}

export function createUserLeaveApi(data) {
  const updateData = transformUserLeave(data)
  return api
    .post('/userleave', updateData, {
      message: __rootT('added_successfully', {
        resource: __rootTc('leave'),
      }),
    })
    .then(transformUserLeave)
}

export function updateUserLeaveApi(data) {
  const updateData = transformUserLeave(data)
  return api
    .patch(`/userleave/${data.id}`, updateData, {
      message: __rootT('updated_successfully', {
        resource: __rootTc('leave'),
      }),
    })
    .then(transformUserLeave)
}

export function deleteLeaveApi(id) {
  return api.delete(`/userleave/${id}`, {
    message: __rootT('cancel_successfully', {
      resource: __rootTc('leave'),
    }),
  })
}

export function getLeaveInfoApi(id) {
  return api.get(`/userleave/${id}`).then(transformUserLeave)
}
